/**
 * Maps drawer/UI state to Location Cleaning Standards API payload (PUT /cleaning-standards/location/{locationId})
 * API spec: windows with days lowercase, time in 12h AM/PM, processes with assignedDevice, timeStandard, ms50Config
 */

import { generateSessionId } from './sessionMigrations';

/**
 * Convert time from 24h (HH:MM) to 12h AM/PM
 * @param {string} time24 - e.g. "08:00", "15:30"
 * @returns {string} e.g. "08:00 AM", "03:30 PM"
 */
export function convertTime24To12(time24) {
  if (!time24 || typeof time24 !== 'string') return '08:00 AM';
  const [hour, min] = time24.split(':').map(Number);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12.toString().padStart(2, '0')}:${(min || 0).toString().padStart(2, '0')} ${period}`;
}

/**
 * Convert time from 12h AM/PM to 24h (HH:MM)
 * @param {string} time12 - e.g. "08:00 AM", "03:30 PM"
 * @returns {string} e.g. "08:00", "15:30"
 */
export function convertTime12To24(time12) {
  if (!time12 || typeof time12 !== 'string') return '08:00';
  const match = time12.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return '08:00';
  let [, hour, min, period] = match;
  hour = parseInt(hour, 10);
  min = parseInt(min, 10) || 0;
  if (period.toUpperCase() === 'PM' && hour !== 12) hour += 12;
  if (period.toUpperCase() === 'AM' && hour === 12) hour = 0;
  return `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
}

/**
 * Map a session's primary and alternative ranges to API format (12h AM/PM)
 * @param {{ primaryRange: { from: string, to: string }, alternativeRanges?: Array<{ from: string, to: string }> }} session
 * @returns {{ name: string, primaryRange: { from: string, to: string }, alternativeRanges: Array<{ from: string, to: string }> }}
 */
function mapSessionToApi(session) {
  const primary = session?.primaryRange || { from: '08:00', to: '12:00' };
  const altRanges = (session?.alternativeRanges || []).slice(0, 2);
  return {
    name: session?.name || 'Session',
    primaryRange: {
      from: convertTime24To12(primary.from),
      to: convertTime24To12(primary.to),
    },
    alternativeRanges: altRanges.map((r) => ({
      from: convertTime24To12(r.from),
      to: convertTime24To12(r.to),
    })),
  };
}

/**
 * Map cleaning process to API format (assignedDevice, timeStandard, ms50Config if MS50)
 * @param {Object} process - { assignedDevice, timeStandard: { minimum, maximum }, deviceConfiguration? }
 * @returns {Object} API process shape
 */
function mapProcessToApi(process) {
  const { assignedDevice, timeStandard, deviceConfiguration } = process || {};
  const min = Number(timeStandard?.minimum ?? 1);
  const max = Number(timeStandard?.maximum ?? 1);
  const out = {
    assignedDevice: assignedDevice || 'V50',
    timeStandard: { minimum: min, maximum: max },
  };
  if (assignedDevice === 'MS50' && deviceConfiguration) {
    const md = deviceConfiguration.moppingDuration || {};
    out.ms50Config = {
      moppingDuration: {
        minimum: Number(md.minimum ?? min),
        maximum: Number(md.maximum ?? max),
      },
      rinseAndWringCycles: Number(deviceConfiguration.rinseWringCycles ?? 1) || 1,
    };
  }
  return out;
}

/**
 * Build API request body from drawer state (windows + other tasks)
 * Only includes windows that have at least one day selected.
 *
 * @param {Array<{ id?: string, name: string, days: string[], sessionType: string, sessions: Array<{ primaryRange: { from: string, to: string }, alternativeRanges?: Array }>, cleaningProcesses?: Array }>} windows - Drawer windows state
 * @param {string} [otherTasksMin] - Min minutes for other tasks (optional)
 * @param {string} [otherTasksMax] - Max minutes for other tasks (optional)
 * @returns {{ windows: Array }} Body for PUT /cleaning-standards/location/{locationId}
 */
export function buildLocationStandardsPayload(windows, otherTasksMin, otherTasksMax) {
  const configuredWindows = (windows || []).filter((w) => w.days && w.days.length > 0);
  const apiWindows = configuredWindows.map((w) => {
    const sessionType = w.sessionType === 'multiple' ? 'multiple' : 'single';
    const sessions = (w.sessions || []).slice(0, sessionType === 'single' ? 1 : 5);
    const win = {
      name: w.name || 'Window',
      days: (w.days || []).map((d) => String(d).toLowerCase()),
      sessionType,
      sessions: sessions.map(mapSessionToApi),
      processes: (w.cleaningProcesses || []).map(mapProcessToApi),
    };
    const minOther = otherTasksMin != null && otherTasksMin !== '' ? parseInt(otherTasksMin, 10) : null;
    const maxOther = otherTasksMax != null && otherTasksMax !== '' ? parseInt(otherTasksMax, 10) : null;
    if (minOther != null && !Number.isNaN(minOther) && maxOther != null && !Number.isNaN(maxOther) && maxOther >= minOther) {
      win.otherTasksTime = { minimum: minOther, maximum: maxOther };
    }
    return win;
  });

  return { windows: apiWindows };
}

/**
 * Map API session (12h) to drawer session (24h)
 */
function mapApiSessionToDrawer(session, index) {
  const primary = session?.primaryRange || { from: '08:00 AM', to: '12:00 PM' };
  const altRanges = (session?.alternativeRanges || []).slice(0, 2);
  return {
    id: session?.id ?? generateSessionId(),
    name: session?.name || `Session ${index + 1}`,
    primaryRange: {
      from: convertTime12To24(primary.from),
      to: convertTime12To24(primary.to),
    },
    alternativeRanges: altRanges.map((r) => ({
      from: convertTime12To24(r.from),
      to: convertTime12To24(r.to),
    })),
  };
}

/**
 * Map API process to drawer process (ms50Config -> deviceConfiguration)
 */
function mapApiProcessToDrawer(process) {
  const { assignedDevice, timeStandard, ms50Config } = process || {};
  const out = {
    assignedDevice: assignedDevice || 'V50',
    timeStandard: {
      minimum: Number(timeStandard?.minimum ?? 1),
      maximum: Number(timeStandard?.maximum ?? 1),
    },
  };
  if (assignedDevice === 'MS50' && ms50Config) {
    out.deviceConfiguration = {
      moppingDuration: ms50Config.moppingDuration || { minimum: 1, maximum: 1 },
      rinseWringCycles: ms50Config.rinseAndWringCycles ?? 1,
    };
  }
  return out;
}

/** Drawer expects day labels like 'Monday', 'Tuesday' (capitalized) to match DAYS[].full */
const DRAWER_DAY_LABELS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];
const API_DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function toDrawerDayFormat(apiDay) {
  const key = String(apiDay || '').trim().toLowerCase();
  const index = API_DAY_KEYS.indexOf(key);
  return index >= 0 ? DRAWER_DAY_LABELS[index] : key ? key.charAt(0).toUpperCase() + key.slice(1) : '';
}

/**
 * Map API standards (GET response) to drawer state for hydration.
 * @param {Object} apiStandards - { windows: Array } from GET /cleaning-standards/location/{id}
 * @returns {{ windows: Array, otherTasksMinDuration: string, otherTasksMaxDuration: string }}
 */
export function apiStandardsToDrawerState(apiStandards) {
  const apiWindows = apiStandards?.windows || [];
  let otherTasksMin = '';
  let otherTasksMax = '';

  const windows = apiWindows.map((w, wi) => {
    const ot = w.otherTasksTime;
    if (ot && typeof ot.minimum === 'number' && typeof ot.maximum === 'number') {
      if (wi === 0) {
        otherTasksMin = String(ot.minimum);
        otherTasksMax = String(ot.maximum);
      }
    }
    const sessions = (w.sessions || []).map((s, si) => mapApiSessionToDrawer(s, si));
    const sessionType = w.sessionType === 'multiple' ? 'multiple' : 'single';
    const sessionsForWindow =
      sessions.length > 0
        ? sessions
        : [
            {
              id: generateSessionId(),
              name: 'Session',
              primaryRange: { from: '08:00', to: '12:00' },
              alternativeRanges: [],
            },
          ];
    return {
      id: `window-${wi + 1}`,
      name: w.name || `Window ${wi + 1}`,
      days: (w.days || []).map(toDrawerDayFormat),
      sessionType,
      sessions: sessionsForWindow,
      cleaningProcesses: (w.processes || []).map(mapApiProcessToDrawer),
    };
  });

  if (windows.length === 0) {
    windows.push({
      id: 'window-1',
      name: 'Window 1',
      days: [],
      sessionType: 'single',
      sessions: [
        {
          id: generateSessionId(),
          name: 'Session',
          primaryRange: { from: '08:00', to: '12:00' },
          alternativeRanges: [],
        },
      ],
      cleaningProcesses: [],
    });
  }

  return {
    windows,
    otherTasksMinDuration: otherTasksMin,
    otherTasksMaxDuration: otherTasksMax,
  };
}

/**
 * Build draft API request body from drawer state (partial/incomplete allowed).
 * Includes all windows (even with no days), empty sessions/processes allowed.
 * For PATCH /cleaning-standards/location/{locationId}/draft
 *
 * @param {Array} windows - Drawer windows state
 * @param {string} [otherTasksMin]
 * @param {string} [otherTasksMax]
 * @returns {{ windows: Array }}
 */
export function buildLocationStandardsDraftPayload(windows, otherTasksMin, otherTasksMax) {
  const allWindows = windows || [];
  const apiWindows = allWindows.map((w) => {
    const sessionType = w.sessionType === 'multiple' ? 'multiple' : 'single';
    const sessions = (w.sessions || []).slice(0, sessionType === 'single' ? 1 : 5);
    const win = {
      name: w.name || 'Window',
      days: (w.days || []).map((d) => String(d).toLowerCase()),
      sessionType,
      sessions: sessions.length ? sessions.map(mapSessionToApi) : [],
      processes: (w.cleaningProcesses || []).map(mapProcessToApi),
    };
    const minOther = otherTasksMin != null && otherTasksMin !== '' ? parseInt(otherTasksMin, 10) : null;
    const maxOther = otherTasksMax != null && otherTasksMax !== '' ? parseInt(otherTasksMax, 10) : null;
    if (minOther != null && !Number.isNaN(minOther) && maxOther != null && !Number.isNaN(maxOther) && maxOther >= minOther) {
      win.otherTasksTime = { minimum: minOther, maximum: maxOther };
    }
    return win;
  });

  return { windows: apiWindows };
}
